import axios from 'axios';

import { calculateEnterExitModalPosition , checkVersion} from '../../mixins/helpers';

const state = {
    autoplay: true,
    galleryItems: [],
    selectedModalItem: null,
    isModalOpen: false,
    isFiltersOpen: false,
    filters: [],
    filterEnabledCount: 0,
    layoutMode: 'feed',
    loadedViewportSize: -1,
    loadedViewportContext: null,
    showVideosOnly: false,
    showImagesOnly: false,
    showFilterControls: true,
    availableCategories: [],
    galleryVersion: 'legacy',
    hotelName: null,
    loading: true,
    hasMixedAssetData: null,
    activeItem: null,
    modalPosition: null,
    modalAnimationLength: 500, // Legacy is default
    modalAnimating: false,
    cmsPreviewMode: null,
}

const getters = {
    getEnabledFilters(state) {
        return state.filters.filter(filter => filter.enabled === true);
    },

    getEnabledFiltersCount(state) {
        let standardFiltersCount = state.filters.filter(filter => filter.enabled === true).length;
        let totalCount = standardFiltersCount;

        if (state.showImagesOnly) {
            totalCount += 1;
        }

        if (state.showVideosOnly) {
            totalCount += 1;
        }

        return totalCount;
    },

    getFilteredItemsCount(state) {
        return state.galleryItems.filter(item => item.visible === true).length;
    },

    getTotalItemsCount(state) {
        return state.galleryItems.length;
    },

    getUnfilteredItems(state) {
        return state.galleryItems;
    },

    getFilteredItems(state) {
        return state.galleryItems.filter(item => item.visible == true);
    },

    getHiddenItems(state) {
        return state.galleryItems.filter(item => item.visible == false);
    },
}

const mutations = {
    updateModalAnimating(state, val) {
        state.modalAnimating = val;
    },
    
    updateLoadingStatus(state, data) {
        state.loading = data
    },

    updateSiteType(state, data) {
        state.galleryVersion = data;

        if (data === "legacy") {
            state.modalAnimationLength = 500;
        } else if (data === "modern") {
            state.modalAnimationLength = 1500;
        }
    },

    updateHotelName(state, data) {
        state.hotelName = data;
    },

    toggleFilterOpenState(state) {
        state.isFiltersOpen = !state.isFiltersOpen;
    },

    updateFilterOpenState(state, data) {
        state.isFiltersOpen = data;
    },

    clearFilters(state) {
        mutations.showAllGalleryItems(state);
        state.showImagesOnly = false;
        state.showVideosOnly = false;
        state.filters.forEach(filter => filter.enabled = false);

        this.commit('gallery/updateFilteredEnabledCount');
    },

    updateFilteredEnabledCount(state) {
        let filterEnabledCount = parseInt(this.getters['gallery/getEnabledFilters'].length);

        if (state.showVideosOnly) {
            filterEnabledCount += 1;
        }

        if (state.showImagesOnly) {
            filterEnabledCount += 1;
        }

        state.filterEnabledCount = filterEnabledCount;
    },

    toggleFilter(state, category) {
        const currentValue = state.filters.filter(filter => filter.name === category.name)[0].enabled;
        state.filters.filter(filter => filter.name === category.name)[0].enabled = !currentValue;

        this.commit('gallery/updateFilteredEnabledCount');    
    },

    toggleShowVideosOnly(state) {
        state.showVideosOnly = !state.showVideosOnly;

        // Due to the desired behaviour, we need to set the opposite asset type to false 
        if (state.showVideosOnly) {
            state.showImagesOnly = false;
        }
    },

    toggleShowImagesOnly(state) {
        state.showImagesOnly = !state.showImagesOnly;

        // Due to the desired behaviour, we need to set the opposite asset type to false
        if (state.showImagesOnly) {
            state.showVideosOnly = false;
        }
    },

    toggleLayoutMode(state) {
        if (state.layoutMode === 'grid') {
            state.layoutMode = 'feed';
        } else {
            state.layoutMode = 'grid';
        }
    },

    toggleGalleryVersion(state) {
        if (state.galleryVersion === 'legacy') {
            state.galleryVersion = 'modern';
        } else {
            state.galleryVersion = 'legacy';
            state.layoutMode = 'feed';
        }
    },

    showAllGalleryItems(state) {
        state.galleryItems.forEach(item => item.visible = true);
    },

    toggleAutoplay(state, data) {
        state.autoplay = !state.autoplay;
    },

    updateFilteredList(state, data) {
        this.commit('gallery/updateFilteredEnabledCount');    

        // If there are no filters set, then we want to show all items
        if (state.filterEnabledCount == 0 && state.showImagesOnly === false && state.showVideosOnly === false) {
            mutations.showAllGalleryItems(state);
        } else {
            state.galleryItems = data;
        }
    },

    updateFilteredItemStyling(state, obj) {
        state.galleryItems = state.galleryItems.map(item => {
            if (item.id === obj.id) {
                item.width = obj.width;
                item.left = obj.left;
                item.aspectRatio = obj.aspectRatio;
                item.aspect = obj.aspect;
                item.marginTop = obj.marginTop;
            }

            return item;
        });
    },

    populateGalleryItems(state, data) {
        state.galleryItems = data;

        this.commit('gallery/updateFilteredEnabledCount');    
    },

    populateNewTestItem(state) {
        const newItem = {
            id: state.galleryItems.length + 1,
            caption: 'A selection of cakes of different sizes and shapes on a table',
            image: '/static/temp/1170x1360.jpg',
            imageWidth: 1170,
            imageHeight: 1360,
            imageAlternativeText: 'A portrait photograph of a table with a selection of edible cakes.',
            fullVideoMp4: '',
            fullVideoWebM: '',
            thumbnailVideoMp4: '',
            thumbnailVideoWebM: '',
            thumbnailImage: '',
            categories: [
                'Hotels'
            ],
            aspectRatio: '',
            left: '',
            marginTop: '',
            width: '',
            visible: true,
        }

        state.galleryItems.push(newItem);
        this.commit('gallery/updateFilteredEnabledCount');    
    },

    populateCategories(state, data) {
        state.availableCategories = data;
        
        // Reset filters to be empty to set toggles
        state.filters = [];

        state.availableCategories.forEach(category => {
            state.filters.push({
                name: category,
                enabled: false
            });
        });
    },

    populateShowFilterControls(state, data) {
        state.showFilterControls = data;
    },

    updateMixedAssets(state, data) {
        state.hasMixedAssetData = data;
    },

    updateModalOpened(state, data) {
        state.isModalOpen = data;
    },

    updateActiveItem(state, data) {
        state.activeItem = data;
        if (data) {
            state.activeItemId = state.activeItem.id;
        }
    },

    updateLayoutMode(state, mode) {
        state.layoutMode = mode;
    },

    updateCMSPreviewMode(state, mode) {
        state.cmsPreviewMode = mode;
    }
}

const actions = {
    checkIfItemHasVideo(item) {
        if (item.fullVideoMp4 || item.fullVideoWebM || item.thumbnailVideoMp4 || item.thumbnailVideoWebM) {
            item.isVideo = true;
        } else {
            item.isVideo = false;
        }

        return item;
    },

    hasMixedAssetData(items) {
        let hasVideos = false;
        let hasImages = false;

        items.forEach(item => {
            if (item.isVideo) {
                hasVideos = true;
            }
            else {
                hasImages = true;
            }
        });

        return (hasVideos === hasImages);
    },

    processGalleryData(data) {
        // Check which items have videos
        data.items = data.items.map(item => {
            actions.checkIfItemHasVideo(item);

            // Add missing style properties
            item.aspectRatio = '';
            item.left = '';
            item.marginTop = '';
            item.width = '';
            item.visible = true;

            return item;
        });

        return data;
    },

    getGalleryData({ commit, rootState }, data) {
        commit('updateLoadingStatus', true);
        
        return new Promise((resolve, reject) => {
            // If we want to ue hardcoded data
            if (data) {
                const processedData = actions.processGalleryData(data);
                const hasMixedAssetData = actions.hasMixedAssetData(processedData.items);

                commit('updateMixedAssets', hasMixedAssetData);
                commit('populateGalleryItems', processedData.items);
                commit('translations/populateTranslationLabels', processedData.labels, { root: true });
                commit('populateCategories', processedData.availableCategories);
                commit('populateShowFilterControls', processedData.showFilterControls);
                commit('updateLoadingStatus', false);
                resolve(data);

            } else {
                const contentItemIdElem = document.querySelector('[data-image-and-video-gallery-id]');
                if (contentItemIdElem === null) throw new Error('No image and video gallery block ID detected');

                const contentItemId = contentItemIdElem.getAttribute('data-image-and-video-gallery-id');
                
                axios.get(`/api/image-video-gallery/${contentItemId}/`, { params: { language: rootState.translations.language } }).then(response => {
                    const processedData = actions.processGalleryData(response.data);
                    const hasMixedAssetData = actions.hasMixedAssetData(processedData.items);

                    commit('updateMixedAssets', hasMixedAssetData);
                    commit('populateGalleryItems', processedData.items);
                    commit('translations/populateTranslationLabels', processedData.labels, { root: true });
                    commit('populateCategories', processedData.availableCategories);
                    commit('populateShowFilterControls', processedData.showFilterControls);

                    commit('updateLoadingStatus', false);
                    resolve(response);
                }).catch(error => {
                    reject(error)
                });
            }
        });
    },

    toggleModernModal({commit}, show) {
        const header = document.querySelector('.header');

        const header__overlay = document.querySelector('.header__overlay');
        if (header__overlay) {
            header__overlay.classList.toggle('js-hide-elem', show);
        }

        // Hide the custom loco scrollbar on the right as we don't allow vertical scrolling in modal
        const locoScrollbar = document.querySelector('.c-scrollbar');
        if (locoScrollbar) {
            locoScrollbar.classList.toggle('js-hide-elem');
        }

        if (window['scrolInstance']) {
            if (show) {

                // Fade the header bar out, its a fade and not display hide to prevent page jumping around
                if (header) {
                    header.classList.toggle('js-fade-elem', show);
                }
                
                // Set timeout is used here to allow the markup to update correctly, perhaps we can use vuejs nextTick instead?
                setTimeout(() => {
                    // Prevent any further scrolling
                    window['scrolInstance'].stop(); 

                    // This stops the scrolling animation immediately
                    window['scrolInstance'].scrollTo(window['scrolInstance'].scroll.instance.scroll.y, { duration: 1, disableLerp: true }); 


                    const modalPopup = document.querySelector('.gallery-modal');
                    // modalPopup.style.maxHeight = window.innerHeight + 'px';
                    
                    // Set modal to start location which will be offscreen, 
                    // this allows us to animate it in from the bottom regardless of how far the user has scrolled down the page
                
                    const modalEntrancePosition = calculateEnterExitModalPosition();
                    modalPopup.style.transition = 'none'; // Disable transition whilst we position it to entrance location
                    modalPopup.style.transform = 'translateY(' + modalEntrancePosition + 'px)';

                    setTimeout(() => {
                        modalPopup.style.transition = ''; // Reset to allow for later transition
                    }, 5);

                    // We have some switching logic regarding how we calculate the position of the modal due to some differences in iOS versions.

                    // A
                    // Setting modal top to 0, with position fixed, and body has no position set it will work

                    // B
                    // Setting modal to calculated top position with position fixed

                    const iOSVersion = checkVersion();     
                    if (iOSVersion) {
                        console.log(iOSVersion);
                    }

                    if (iOSVersion != null && (iOSVersion.version != 14.5 && iOSVersion.version != 15.5) && iOSVersion.version < 16.0 && iOSVersion.device === 'iPad') {  // if OLD IPAD
                        // state.modalPosition = window['scrolInstance'].scroll.instance.scroll.y;
                        state.modalPosition = 0;
                        modalPopup.style.transform = 'translateY(' + state.modalPosition + 'px)';
                        document.body.classList.add('gallery-modal-open');
                    }
                    else if (iOSVersion != null && (iOSVersion.version == 14.5 || iOSVersion.version == 15.5) && iOSVersion.device === 'iPad') {  // iPad 14.5
                        state.modalPosition = window['scrolInstance'].scroll.instance.scroll.y;
                        modalPopup.style.transform = 'translateY(' + state.modalPosition + 'px)';
                        document.body.classList.add('gallery-modal-open');
                    }
                    else if (iOSVersion != null && iOSVersion.version <= 16) {  // IF OLD iPHONE
                        state.modalPosition = 0;

                        setTimeout(() => {
                            modalPopup.style.transform = 'translateY(' + state.modalPosition + 'px)';
                            document.body.classList.add('gallery-modal-open');
                        }, 15);
                    } else if (window.innerWidth < 768 && iOSVersion != null && iOSVersion.version > 16) { // if NEW 
                        setTimeout(() => {
                            // Move the modal to page scroll which is needed for newer versions
                            state.modalPosition = window.scrollY;
                            modalPopup.style.transform = 'translateY(' + state.modalPosition + 'px)';

                            window.scrollTo({ left: 0, top: state.modalPosition, behavior: "instant" });
                            document.body.classList.add('gallery-modal-open');
                        }, 5);
                    } else if (window.innerWidth < 768) { // If no iOS version detected
                        setTimeout(() => {
                            state.modalPosition = window.scrollY;
                            modalPopup.style.transform = 'translateY(' + state.modalPosition + 'px)';
                            document.body.classList.add('gallery-modal-open');
                        }, 15);

                        // We check the scroll twice to ensure it has correctly gone to the location
                        setTimeout(() => {
                            window.scrollTo({ left: 0, top: state.modalPosition, behavior: "instant" });
                            document.body.classList.add('gallery-modal-open');
                        }, 150);

                        setTimeout(() => {
                            window.scrollTo({ left: 0, top: state.modalPosition, behavior: "instant" });
                            document.body.classList.add('gallery-modal-open');
                        }, 250);
                    } else {
                        // For desktop we want to use the locoscroll location as that handles scroll positions
                        setTimeout(() => {
                            state.modalPosition = window['scrolInstance'].scroll.instance.scroll.y;
                            modalPopup.style.transform = 'translateY(' + state.modalPosition + 'px)';
                            document.body.classList.add('gallery-modal-open');
                        }, 15);
                    }    
                }, 5);
            } else {
                setTimeout(() => {
                    document.body.style.position = ''; // Remove posiition fixed after CSS animation has finished

                    const mainContent = document.querySelector('main.main-content');
                    if (mainContent) {
                        mainContent.classList.remove('js-elevate-zindex');
                    }

                    setTimeout(() => {
                        if (header) {
                            header.classList.toggle('js-fade-elem', show);
                        }

                    }, 50);

                    window['scrolInstance'].start();
                }, state.modalAnimationLength);
            }
        }
    },
}

module.exports = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};